import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { useT, useLanguage } from "@/i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  back: { en: "Back to site", sr: "Nazad na sajt" },
  title: { en: "Find your reservation", sr: "Pronađite vašu rezervaciju" },
  desc: {
    en: "Enter the email you used and your contract number (e.g. WI-RZ-0006). We'll take you straight to your reservation.",
    sr: "Unesite email koji ste koristili i broj vašeg ugovora (npr. WI-RZ-0006). Odvešćemo vas direktno do vaše rezervacije.",
  },
  email: { en: "Email *", sr: "Email *" },
  contract: { en: "Contract number *", sr: "Broj ugovora *" },
  submit: { en: "Find reservation", sr: "Pronađi rezervaciju" },
  searching: { en: "Searching…", sr: "Pretraga…" },
  notFound: {
    en: "No reservation found with that email and contract number. Double-check both fields and try again.",
    sr: "Nije pronađena rezervacija sa tim email-om i brojem ugovora. Proverite oba polja i pokušajte ponovo.",
  },
  fillFields: { en: "Both fields are required.", sr: "Oba polja su obavezna." },
};

export default function Lookup() {
  const t = useT();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [contract, setContract] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !contract.trim()) {
      toast.error(t(T.fillFields));
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/pre-agreements/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          contract_number: contract.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Not found");
      navigate(`/r/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || t(T.notFound));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F]">
      <header className="border-b border-[#E8E7E2] bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="lookup-back-home" className="inline-flex items-center gap-2 text-sm font-medium text-[#62646B] hover:text-[#0A0A0F]">
            <ArrowLeft size={14} /> {t(T.back)}
          </Link>
          <div className="flex items-center gap-3">
            <img src="/img/winn-coin.png" alt="Winfield" className="w-8 h-8 object-contain" />
            <span className="font-heading font-semibold tracking-tight">$WINN</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10 wf-glow"
        >
          <div className="flex items-center gap-3">
            <Search size={20} className="text-[#0A0A0F]" />
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t(T.title)}</h1>
          </div>
          <p className="text-sm text-[#62646B] leading-relaxed mt-2 max-w-xl">{t(T.desc)}</p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="lookup-form">
            <div>
              <label className="text-xs text-[#62646B] block mb-1.5">{t(T.email)}</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="lookup-email"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors text-[#0A0A0F]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs text-[#62646B] block mb-1.5">{t(T.contract)}</label>
              <input
                type="text"
                value={contract}
                onChange={(e) => setContract(e.target.value.toUpperCase())}
                data-testid="lookup-contract"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors text-[#0A0A0F] font-mono"
                placeholder="WI-RZ-0006"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="lookup-submit"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {busy ? (<><Loader2 size={14} className="animate-spin" />{t(T.searching)}</>) : (<><Search size={14} />{t(T.submit)}</>)}
            </button>
          </form>
        </motion.div>
      </div>
    </main>
  );
}
